import React from 'react';
import { ExportOutlined } from '@ant-design/icons';
import { ModalWidth, ROW_GUTTER, VALIDATE_REQUIRED } from '@/config';
import { Col, Form, Input, message, Modal, Radio, Row, Select, Spin } from 'antd';
import { useTranslation } from 'react-i18next';
import { TripDetailDto } from '@/__generated';
import { apiSdk } from '@/httpClient';
import { defaultApiErrorHandle } from '@/helpers/errorHandler';
import { exportService } from '@/services/exportService';

type Props = {
  trip: TripDetailDto;
  onClose: () => void;
};

export default function ExportPassengerModal({ trip, onClose }: Props) {
  const { t } = useTranslation();
  const [loading, setLoading] = React.useState<boolean>(false);
  const [form] = Form.useForm();

  const exportPdf = (formValues: any) => {
    exportService.exportTripPassenger({ tripId: trip.id, ...formValues }).toPdf();
    onClose();
  };

  const exportByEmail = (formValues: any) => {
    setLoading(true);
    apiSdk.TripDetailService.sendTravelerReportByEmail({ body: { tripId: trip.id, ...formValues } })
      .then(res => {
        if (res.success) {
          message.success(t('Send report to email successfully!'));
          onClose();
        } else {
          defaultApiErrorHandle(res);
        }
      })
      .catch(defaultApiErrorHandle)
      .finally(() => {
        setLoading(false);
      });
  };
  const type = Form.useWatch('type', form);
  const sourceId = Form.useWatch('sourceId', form);

  const fromStations = React.useMemo(() => {
    return [{ value: trip.sourceId, label: trip.sourceName }, ...(trip?.tripStops?.map(s => ({ label: s.stationName, value: s.stationId })) || [])];
  }, [trip]);
  const toStations = React.useMemo(() => {
    let indexOfFrom = trip?.tripStops?.findIndex(x => x.stationId === sourceId) || -1;
    return [
      ...(trip?.tripStops?.filter((_, index) => index > indexOfFrom).map(s => ({ label: s.stationName, value: s.stationId })) || []),
      { value: trip.destinationId, label: trip.destinationName },
    ];
  }, [trip, sourceId]);

  return (
    <Modal
      open={true}
      maskClosable={false}
      width={ModalWidth.small}
      className="nta-custom-form"
      title={t('tripDetailsPage_ExportPassenger')}
      forceRender={true}
      style={{ top: 10 }}
      okText={t('Export')}
      cancelText={t('Close')}
      okButtonProps={{
        icon: <ExportOutlined />,
        className: 'save-btn',
        loading: loading,
      }}
      onCancel={() => {
        onClose();
      }}
      onOk={() => {
        form
          .validateFields()
          .then(({ type, ...props }) => {
            if (type === 1) {
              exportPdf(props);
            } else {
              exportByEmail(props);
            }
          })
          .catch(() => {});
      }}
    >
      <Spin spinning={loading}>
        <Form form={form} layout="vertical" initialValues={{ type: 1, sourceId: trip.sourceId, destinationId: trip.destinationId }}>
          <Row gutter={ROW_GUTTER}>
            <Col md={24}>
              <Form.Item name="type" label={t('Select export method')}>
                <Radio.Group>
                  <Radio value={1}>{t('tripDetailsPage_ExportPassengerPdf')}</Radio>
                  <Radio value={2}>{t('tripDetailsPage_SendReportToEmail')}</Radio>
                </Radio.Group>
              </Form.Item>
              {type === 2 ? (
                <Form.Item rules={[{ required: true, message: VALIDATE_REQUIRED }]} label={t('base.Email')} name="email">
                  <Input />
                </Form.Item>
              ) : null}
              <Form.Item rules={[{ required: true, message: VALIDATE_REQUIRED }]} label={t('base.From')} name="sourceId">
                <Select
                  options={fromStations}
                  onChange={() => {
                    form.setFieldValue('destinationId', null);
                  }}
                />
              </Form.Item>
              <Form.Item rules={[{ required: true, message: VALIDATE_REQUIRED }]} label={t('base.To')} name="destinationId">
                <Select options={toStations} />
              </Form.Item>
            </Col>
          </Row>
        </Form>
      </Spin>
    </Modal>
  );
}
